import { useEffect, useState } from "react";
import type { ModuleName } from "@dentacore/shared-types";
import {
  ADD_ON_MODULES,
  bandsProblem,
  inForce,
  MODULE_LABELS,
  PLATFORM_CURRENCY,
} from "@dentacore/shared-types";

import styles from "./operator-page.module.css";
import { OverviewPanel } from "./overview-panel";
import { listPrices, saveBands, saveBundlePrice, saveModulePrice } from "./operator-api";
import type { PriceListRow } from "./operator-api";
import { priceCents, priceText } from "./price-format";

/**
 * **The platform price list** (STORY-190a, BL-D2 to BL-D6, BL-D9). Monthly, in
 * one currency (BL-D3). A save takes effect on the first of next month — except
 * for an item that has never had a price, which starts this month — and the page
 * says which, so nobody expects a change to land today.
 */
export function PricesSection(): React.JSX.Element {
  const [rows, setRows] = useState<PriceListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void listPrices()
      .then(setRows)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load the prices");
      });
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const inForceNow = inForce(rows ?? [], today);
  const pending = (rows ?? []).filter((r) => r.effectiveFrom > today);

  const land = (next: PriceListRow[]): void => {
    setRows(next);
    setError(null);
    setNotice(
      "Saved. A change to a price that already existed takes effect on the first of next month.",
    );
  };
  const fail = (cause: unknown): void => {
    setNotice(null);
    setError(cause instanceof Error ? cause.message : "Could not save");
  };

  return (
    <div className={styles.console} data-testid="prices-section">
      <header className={styles.head}>
        <h1 className={styles.title}>Prices</h1>
        <p className={styles.muted}>
          Monthly, in {PLATFORM_CURRENCY}, for every clinic without its own price. Nothing here is
          charged by DentaCore: you invoice outside the product.
        </p>
      </header>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {notice !== null && <p className={styles.muted}>{notice}</p>}
      <OverviewPanel />
      {rows === null ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <>
          <BandsEditor
            current={inForceNow.filter((r) => r.kind === "BASE")}
            onSaved={land}
            onError={fail}
          />
          <section className={styles.panel}>
            <span className={styles.panelTitle}>Add-ons</span>
            {ADD_ON_MODULES.map((module) => (
              <PriceRowEditor
                key={module}
                label={MODULE_LABELS[module]}
                current={
                  inForceNow.find((r) => r.kind === "MODULE" && r.module === module)?.priceCents ??
                  null
                }
                testId={`price-${module}`}
                onSave={(cents) => saveModulePrice(module as ModuleName, cents).then(land, fail)}
              />
            ))}
            <PriceRowEditor
              label="Everything bundle (replaces the three)"
              current={inForceNow.find((r) => r.kind === "BUNDLE")?.priceCents ?? null}
              testId="price-bundle"
              onSave={(cents) => saveBundlePrice(cents).then(land, fail)}
            />
          </section>
          {pending.length > 0 && (
            <section className={styles.panel} data-testid="prices-pending">
              <span className={styles.panelTitle}>Changes waiting</span>
              <ul className={styles.rows}>
                {pending.map((r) => (
                  <li key={r.id} className={styles.row}>
                    <span className={styles.rowName}>
                      {r.kind === "BASE"
                        ? `Base, ${String(r.branchesFrom)}${r.branchesTo === null ? "+" : r.branchesTo === r.branchesFrom ? "" : `–${String(r.branchesTo)}`} branches`
                        : r.kind === "BUNDLE"
                          ? "Everything bundle"
                          : MODULE_LABELS[r.module as ModuleName]}{" "}
                      · {priceText(r.priceCents)}
                    </span>
                    <span className={styles.rowSub}>from {r.effectiveFrom}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function PriceRowEditor({
  label,
  current,
  testId,
  onSave,
}: {
  label: string;
  current: number | null;
  testId: string;
  onSave: (cents: number) => Promise<unknown>;
}): React.JSX.Element {
  const [text, setText] = useState(current === null ? "" : (current / 100).toFixed(2));
  useEffect(() => {
    setText(current === null ? "" : (current / 100).toFixed(2));
  }, [current]);
  const cents = priceCents(text);
  return (
    <div className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.rowName}>{label}</span>
        <span className={styles.rowSub}>
          {current === null ? "No price set" : `In force: ${priceText(current)} a month`}
        </span>
      </div>
      <input
        value={text}
        inputMode="decimal"
        aria-label={`${label} price per month`}
        data-testid={`${testId}-input`}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <button
        type="button"
        className={styles.ghost}
        disabled={cents === null}
        data-testid={`${testId}-save`}
        onClick={() => {
          if (cents !== null) void onSave(cents);
        }}
      >
        Save
      </button>
    </div>
  );
}

interface BandDraft {
  from: string;
  to: string;
  price: string;
}

function BandsEditor({
  current,
  onSaved,
  onError,
}: {
  current: PriceListRow[];
  onSaved: (rows: PriceListRow[]) => void;
  onError: (cause: unknown) => void;
}): React.JSX.Element {
  const toDrafts = (rows: PriceListRow[]): BandDraft[] =>
    [...rows]
      .sort((a, b) => (a.branchesFrom ?? 0) - (b.branchesFrom ?? 0))
      .map((r) => ({
        from: String(r.branchesFrom ?? ""),
        to: r.branchesTo === null ? "" : String(r.branchesTo),
        price: (r.priceCents / 100).toFixed(2),
      }));
  const [drafts, setDrafts] = useState<BandDraft[]>(() => toDrafts(current));
  useEffect(() => {
    setDrafts(toDrafts(current));
  }, [current.map((r) => r.id).join(",")]);

  const parsed = drafts.map((d) => ({
    branchesFrom: Number(d.from),
    branchesTo: d.to.trim() === "" ? null : Number(d.to),
    priceCents: priceCents(d.price),
  }));
  const bad =
    parsed.some(
      (b) =>
        !Number.isInteger(b.branchesFrom) ||
        b.priceCents === null ||
        (b.branchesTo !== null && !Number.isInteger(b.branchesTo)),
    ) ||
    bandsProblem(
      parsed.map((b) => ({ branchesFrom: b.branchesFrom, branchesTo: b.branchesTo })),
    ) !== null;
  const problem = bad
    ? (bandsProblem(
        parsed.map((b) => ({ branchesFrom: b.branchesFrom, branchesTo: b.branchesTo })),
      ) ?? "Check every number")
    : null;

  return (
    <section className={styles.panel} data-testid="bands-editor">
      <span className={styles.panelTitle}>Base plan, by number of active branches</span>
      <span className={styles.muted}>
        One flat monthly price for the whole clinic. Leave the last band's upper limit empty.
      </span>
      {drafts.map((d, i) => (
        <div key={i} className={styles.row}>
          <input
            value={d.from}
            aria-label={`Band ${String(i + 1)} from`}
            data-testid={`band-from-${String(i)}`}
            onChange={(e) =>
              setDrafts(drafts.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))
            }
          />
          <span>to</span>
          <input
            value={d.to}
            placeholder="and up"
            aria-label={`Band ${String(i + 1)} to`}
            data-testid={`band-to-${String(i)}`}
            onChange={(e) =>
              setDrafts(drafts.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))
            }
          />
          <input
            value={d.price}
            inputMode="decimal"
            aria-label={`Band ${String(i + 1)} price per month`}
            data-testid={`band-price-${String(i)}`}
            onChange={(e) =>
              setDrafts(drafts.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))
            }
          />
          <button
            type="button"
            className={styles.ghost}
            onClick={() => setDrafts(drafts.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      {problem !== null && (
        <span className={styles.warn} data-testid="bands-problem">
          {problem}
        </span>
      )}
      <div className={styles.row}>
        <button
          type="button"
          className={styles.ghost}
          onClick={() => setDrafts([...drafts, { from: "", to: "", price: "" }])}
        >
          Add a band
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={bad}
          data-testid="bands-save"
          onClick={() => {
            void saveBands(
              parsed.map((b) => ({
                branchesFrom: b.branchesFrom,
                branchesTo: b.branchesTo,
                priceCents: b.priceCents ?? 0,
              })),
            ).then(onSaved, onError);
          }}
        >
          Save the bands
        </button>
      </div>
    </section>
  );
}
